import { useEffect } from "react";
import idx from "idx";
import useRouter from "found/useRouter";
import BrowserLink from "found/Link";
import Link from "@material-ui/core/Link";

import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Card from "@material-ui/core/Card";
import CardActions from "@material-ui/core/CardActions";
import CardContent from "@material-ui/core/CardContent";
import CardHeader from "@material-ui/core/CardHeader";
import Container from "@material-ui/core/Container";
import Divider from "@material-ui/core/Divider";
import FormField from "@material-ui/core/TextField";
import FormButton from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";

import graphql from "babel-plugin-relay/macro";
import { useMutation } from "relay-hooks";

import Page from "common/Page";
import useForm from "common/hooks/useForm";
import useAuth from "common/hooks/useAuth";
import SnackBar, { openSnackbar } from "common/SnackBar";
import FormFeedback from "common/FormFeedback";
import CircularProgress from "common/FacebookProgress";
import logo from "common/images/hfa_logo.svg";

import parseHtmlToJsx from "util/parseHtmlToJsx";

import { LoginUserMutationResponse as LoginResponse } from "./__generated__/LoginUserMutation.graphql";

const mutation = graphql`
  mutation LoginUserMutation($input: AuthUserInput!) {
    authUser(input: $input) {
      user {
        id
        email
        firstName
        lastName
        role
        salesforceId
        companyName
      }
      token
      error
    }
  }
`;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {},
    formContainer: {
      marginBottom: theme.spacing(4),
    },
    paper: {
      marginTop: theme.spacing(8),
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      backgroundColor: "white",
      borderRadius: "8px",
      // padding: theme.spacing(2),
    },
    avatar: {
      margin: theme.spacing(1),
      backgroundColor: theme.palette.secondary.main,
    },
    form: {
      // width: "100%", // Fix IE 11 issue.
      // marginTop: theme.spacing(1),
    },
    card: {},
    cardHeader: {
      padding: theme.spacing(1, 2),
      textAlign: "center",
    },
    cardTitle: {
      fontSize: "1.25rem",
    },
    cardSubheader: {
      fontSize: "0.875rem",
    },
    submit: {
      margin: theme.spacing(3, 0, 2),
    },
    feedback: {
      marginTop: theme.spacing(2),
      textTransform: "capitalize",
    },
    title: {
      fontSize: 14,
    },
    error: {
      textTransform: "capitalize",
    },
    buttonWrapper: {
      position: "relative",
      padding: theme.spacing(2),
    },
    buttonProgress: {
      position: "absolute",
      top: "50%",
      left: "50%",
      marginTop: -9,
      marginLeft: -12,
    },
    option: {
      fontSize: 15,
      "& > span": {
        marginRight: 10,
        fontSize: 18,
      },
    },
    contactInfo: {
      fontWeight: theme.typography.fontWeightBold,
    },
  })
);

const schema = {
  email: "",
  password: "",
};

// Create your own validationSchema schema property should be the same in
// validationSchema in-order a validation to works in your input.
const validatorSchema = {
  email: {
    required: true,
    validator: {
      func: (value: string) => value.length > 4,
      error: "Email is too short",
    },
  },
  password: {
    required: true,
    validator: {
      func: (value: string) => value.length > 5,
      error: "Password is too short",
    },
  },
};

interface IProps {
  data?: any | null;
  error?: any | null;
}

export default function LoginUser(props: IProps) {
  const classes = useStyles();
  const { isLoggedin, login, getCurrentUser } = useAuth();
  const { match, router } = useRouter();

  const { redirect, expired } = match?.location?.query || {};

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const {
    disable,
    values,
    errors,
    setError,
    // setValue,
    handleOnChange,
    handleOnSubmit,
  } = useForm(schema, validatorSchema, onSubmitForm);

  const [mutate, { loading }] = useMutation(mutation, {
    onCompleted: ({ authUser }: LoginResponse) => {
      if (authUser && authUser.error) {
        if (authUser.error === "ERR_FIRST_TIME_LOG_IN") {
          router.push("/user/createPassword/message");
        } else {
          setError("error", authUser.error);
          openSnackbar("Login failed");
        }
      }

      if (authUser && authUser.token && authUser.user) {
        localStorage.setItem(
          "user",
          JSON.stringify({
            ...authUser.user,
            companySalesforceId: authUser?.company?.salesforceId || "",
          })
        );
        login(authUser.token);

        // window.location.reload();
      }
    },
    onError: (error) => {
      setError("error", error);
      openSnackbar("Login failed");
    },
  });

  useEffect(() => {
    if (expired) {
      setError(
        "error",
        `Unfortunately your account is no longer active. Please contact us on <a class="MuiTypography-root MuiLink-root MuiLink-underlineHover MuiTypography-colorPrimary" href="mailto:support@hedgefundalert.com">support@hedgefundalert.com</a> to discuss how to reactivate your subscription.`
      );
    }
  }, [expired, setError]);

  useEffect(() => {
    if (isLoggedin) {
      //   const targetBuild = process.env.REACT_APP_TARGET_BUILD;
      //   if (targetBuild === "admin") {
      //     // Redirect for Admin
      //     // window.location.href = `${redirectUrl}/admin/loading`;
      //     router.push("/admin/dashboard");
      //   } else {
      //     // Redirect for Public
      //     router.push("/publications");
      //   }
      const redirectUrl =
        process.env.REACT_APP_BASE_DOMAIN_ADMIN ||
        window.location?.origin! ||
        window.location?.href!;
      const user = getCurrentUser();

      if (redirect) {
        return router.replace(atob(decodeURIComponent(redirect)));
      }
      if (user && user?.role! && user.role === "admin") {
        // Redirect for Admin
        window.location.href = `${redirectUrl}/admin/dashboard`;
      } else {
        // Redirect for Public
        router.push("/publications");
      }
    }
  }, [isLoggedin, router, getCurrentUser, redirect]);

  useEffect(() => {
    document.title = "Login";
  }, []);

  // finally call the mutation to save to backend
  function onSubmitForm(data?: any) {
    // console.log('No errors, submit callback called!');
    mutate({
      variables: {
        input: {
          email: data.email,
          password: data.password,
        },
      },
    });
  }

  const errorCheck = (key: string) => {
    const message = idx(
      errors,
      (x: any) => x!.error!.extensions!.validation[key]
    );
    return message ? true : false;
  };

  const hasError = (inputName: string) => !!(errors && errors[inputName]);

  if (isLoggedin) {
    return (
      <Page className={classes.root} title="Login">
        <Container maxWidth="xs">
          <Box p={1} m={1} justifyContent="center" textAlign="center">
            Loading......
          </Box>
        </Container>
      </Page>
    );
  }

  return (
    <Page className={classes.root} title="Login">
      <Container maxWidth="xs" className={classes.formContainer}>
        <Box p={1} m={1} justifyContent="center" textAlign="center">
          <img
            className={classes.root}
            src={logo}
            alt={process.env.REACT_APP_SITE_NAME}
          />
        </Box>

        <Card className={classes.card} raised>
          <CardHeader
            title="Client Login"
            className={classes.cardHeader}
            titleTypographyProps={{ className: classes.cardTitle }}
          />
          {/* <Divider /> */}
          <CardContent>
            <SnackBar variant="error" />

            <form onSubmit={handleOnSubmit} className={classes.form}>
              <div className={classes.card}>
                <FormField
                  name="email"
                  type="email"
                  label="Email"
                  value={values!.email}
                  onChange={handleOnChange}
                  error={errorCheck("Email")}
                  variant="outlined"
                  margin="normal"
                  required
                  fullWidth
                />

                <FormField
                  name="password"
                  type="password"
                  label="Password"
                  value={values!.password}
                  onChange={handleOnChange}
                  error={errorCheck("Password")}
                  variant="outlined"
                  margin="normal"
                  required
                  fullWidth
                />

                {hasError("error") ? (
                  <FormFeedback className={classes.feedback} error>
                    {idx(errors, (x: any) => parseHtmlToJsx(x!.error)) || <></>}
                  </FormFeedback>
                ) : null}
              </div>

              <div className={classes.buttonWrapper}>
                <FormButton
                  disabled={disable || loading}
                  type="submit"
                  // size="large"
                  color="primary"
                  variant="contained"
                  fullWidth
                >
                  Login
                </FormButton>
                {loading && (
                  <CircularProgress className={classes.buttonProgress} />
                )}
              </div>
            </form>
          </CardContent>

          <Divider />
          <CardActions>
            <Grid container>
              <Grid item xs>
                <Link component={BrowserLink} to="/user/password/forgot">
                  Click here if you have forgotten your password or have not yet
                  been given a password.
                </Link>
              </Grid>
            </Grid>
          </CardActions>
        </Card>
      </Container>

      <Container>
        <Typography
          gutterBottom
          component="div"
          variant="body2"
          align="center"
          className={classes.contactInfo}
        >
          Need help or would like more information?
        </Typography>

        <Typography
          gutterBottom
          component="div"
          variant="body2"
          align="center"
          className={classes.contactInfo}
        >
          Email us at{" "}
          <Link href="mailto:support@hedgefundalert.com">
            support@hedgefundalert.com
          </Link>{" "}
          or call <Link href="tel:+1 646 891 2126">+1 646 891 2126</Link>
        </Typography>
      </Container>
    </Page>
  );
}
